import { useAuth } from "@/auth/hooks/useAuth";
import useScopedDashboardAccess from "@/auth/hooks/useScopedDashboardAccess";
import { useHidePostActions } from "@/auth/hooks/useHidePostActions";
import { Button } from "@/components/ui/button";
import { useModal } from "@/dashboard/store/store";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ProjectSummary from "./ProjectSummary";

const ProjectList: FC<{
  projects: any;
  programId: number;
  programsParam?: any;
  onDeleteProject?: (
    programId: number,
    projectId: number,
    deleteRequest: () => Promise<any>,
  ) => Promise<any>;
  onDuplicateProject?: (
    programId: number,
    project: any,
    duplicateRequest: () => Promise<any>,
  ) => Promise<any>;
}> = ({
  projects,
  programId,
  programsParam,
  onDeleteProject,
  onDuplicateProject,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { setOpenModal } = useModal();
  const { t } = useTranslation("dashboard", {
    keyPrefix: "programs",
  });
  const [programs, setPrograms] = useState([]);

  const { fetchData } = useFetchDataV2(``);

  // const { data: programs } = useFetchDataV2<ProgramType[]>(
  //   `${import.meta.env.VITE_API_ENDPOINT}/programs`,
  //   `programs`,
  //   1000 * 60 * 60
  // );

  useEffect(() => {
    if (Array.isArray(programsParam)) {
      setPrograms(programsParam);
    } else {
      fetchData(`${import.meta.env.VITE_API_ENDPOINT}/programs`).then(
        (res) => {
          setPrograms(Array.isArray(res) ? res : []);
        },
      );
    }
  }, []);

  const programsList = Array.isArray(programs) ? programs : [];
  const projectsList = Array.isArray(projects) ? projects : [];
  const currentProgram = programsList.find((item: any) => Number(item?.id) === Number(programId));
  const isDirectProgramOwner =
    currentProgram?.carrier?.id != null &&
    user?.id != null &&
    Number(currentProgram.carrier.id) === Number(user.id);
  const hidePostActions = useHidePostActions(
    currentProgram?.carrier?.id ?? null,
  );
  const { canManageScope } = useScopedDashboardAccess(
    currentProgram?.carrier?.id ?? null,
  );
  const canManageCurrentProgram =
    isDirectProgramOwner || (canManageScope && !hidePostActions);
  const currentProgramName = currentProgram?.name ? String(currentProgram.name) : "";
  const openCreateProjectModal = () => {
    setOpenModal({
      name: "projectCreationChoice",
      value: true,
      programId,
      programName: currentProgramName,
    });
  };

  return (
    <div>
      <div className="grid grid-cols-3 w-full items-start">
        {projectsList.map((project: any, key: number) => {
          const isOptimistic = Boolean(project?.__optimistic);
          const isPersonalized = Boolean(
            project?.isPersonnalized ?? project?.personnalized ?? project?.is_personnalized ?? false,
          );
          const statusNumber = Number(project?.status);
          const shouldOpenDynamicForm =
            isPersonalized &&
            Number.isFinite(statusNumber) &&
            statusNumber !== 3;
          const projectLink = shouldOpenDynamicForm
            ? `/dashboard/project/create-custom?programId=${programId}&projectId=${project.id}`
            : `/dashboard/project/${project.id}`;

          return (
            <div className="mb-4 px-2 " key={key}>
              <ProjectSummary
                key={key}
                id={project.id}
                type={project.type}
                picture={
                  project?.defaultImage
                    ? import.meta.env.VITE_MEDIA_BASE + project?.defaultImage?.uri
                    : null
                }
                title={project.name}
                link={isOptimistic ? "#" : projectLink}
                status={project.status}
                onSelect={() => {
                  if (!isOptimistic) {
                    navigate(projectLink);
                  }
                }}
                typeProjectImage={project?.type?.image?.uri ?? null}
                programsList={programsList}
                programId={programId}
                soldStatus={project.soldStatus}
                ownerCarrierId={currentProgram?.carrier?.id ?? null}
                onDeleteProject={onDeleteProject}
                onDuplicateProject={onDuplicateProject}
                project={project}
              />
            </div>
          );
        })}
        <div className="mb-4 h-full px-2 ">
          {canManageCurrentProgram && (
              <>
                <Button
                  variant="outline"
                  className=" flex size-full h-[170px] items-center justify-center rounded-[15px] border"
                  style={{ background: "#EFEFEF" }}
                  onClick={openCreateProjectModal}
                >
                  <div className="flex w-max items-center">
                    <svg
                        width="48"
                        height="36"
                        viewBox="0 0 48 36"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                      <path
                        d="M4.61257 36C3.34411 36 2.25863 35.5597 1.35609 34.6793C0.452031 33.7973 0 32.7375 0 31.5V4.5C0 3.2625 0.452031 2.20351 1.35609 1.323C2.25863 0.441 3.34411 0 4.61257 0H16.5476C17.1626 0 17.7492 0.1125 18.3073 0.3375C18.8639 0.5625 19.3536 0.881249 19.7765 1.29375L23.0628 4.5H41.5131C42.7815 4.5 43.8679 4.941 44.772 5.823C45.6744 6.70351 46.1257 7.7625 46.1257 9H21.1602L16.5476 4.5H4.61257V31.5L9.16747 16.7062C9.47498 15.7312 10.0423 14.9527 10.8695 14.3707C11.6952 13.7902 12.6077 13.5 13.6071 13.5H43.3581C44.9341 13.5 46.1742 14.109 47.0782 15.327C47.9806 16.5465 48.2205 17.8687 47.7976 19.2938L43.6463 32.7938C43.3389 33.7687 42.7723 34.5472 41.9467 35.1293C41.1195 35.7098 40.2063 36 39.2068 36H4.61257ZM9.45576 31.5H39.2068L43.3581 18H13.6071L9.45576 31.5Z"
                        fill="#D4D4D4"
                      />
                      <rect
                        x="25"
                        y="19"
                        width="3"
                        height="11"
                        rx="1"
                        fill="#D9D9D9"
                      />
                      <rect
                        x="32"
                        y="23"
                        width="3"
                        height="11"
                        rx="1"
                        transform="rotate(90 32 23)"
                        fill="#D9D9D9"
                      />
                    </svg>
                  </div>
                </Button>
                <div className="text-md mt-4 flex cursor-pointer items-center gap-2 font-semibold text-foreground opacity-70">
                  {t("Ajouter")}
                </div>
              </>
            )}
        </div>
      </div>
    </div>
  );
};

export default ProjectList;
